import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {Stack} from "@mui/material";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import Property from "crqlar-react-common/dist/components/Property";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {CRQLAR_EXTRA_FIELD_SOURCE, ExtraField} from "crqlar-react-common/dist/models/profile/extraField.model";

interface Props {
    data: UseEntityData<Profile>;
}

/**
 * Step 3c — PMS Extra Fields section. Displays the free-form `(key, value)` attributes
 * the PMS attaches to a profile beyond Crqlar's standard model.
 *
 * Editability rule: entries with `source !== "crqlar"` are read-only — the PMS owns them
 * and Crqlar would lose those edits on the next sync. Entries with `source === "crqlar"`
 * (or missing source on entries the user just added in this session) are fully editable.
 * Empty profile section shows an Add button only.
 */
const ExtraFields = (props: Props) => {
    const {t} = useTranslation();
    const profile = props.data.item;
    const setProfile = props.data.setItem;

    const updateField = (field: keyof Profile, value: any) => {
        setProfile({...profile, [field]: value});
    };

    const updateExtraField = (index: number, patch: Partial<ExtraField>) => {
        const extraFields = [...(profile.extraFields || [])];
        extraFields[index] = {
            ...extraFields[index],
            ...patch,
            updatedAt: new Date(),
        };
        updateField('extraFields', extraFields);
    };

    return (
        <Stack gap={{xs: 3, sm: 2.5}}>
            {
                profile.extraFields?.map((extraField, index) => {
                    const source = extraField.source;
                    // Read-only unless the entry originated in Crqlar (user-added). A null
                    // source is treated as read-only — safer default for connector code
                    // that forgot to set the source.
                    const editable = source === CRQLAR_EXTRA_FIELD_SOURCE;
                    return (
                        <Property
                            orientation={'vertical'}
                            key={index}
                            title={t('Extra field') + ' ' + (index + 1)}
                            subtitle={source || undefined}
                            sx={{
                                title: {
                                    mt: 2,
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Stack gap={1} width={'100%'}>
                                <Property title={t('Key')} labelAlignment={'top'}>
                                    <TextField
                                        id={`extra-field-${index}-key`}
                                        data-testid={`extra-field-${index}-key`}
                                        size={'small'}
                                        value={extraField.key || ''}
                                        onChange={(e) => updateExtraField(index, {key: e.target.value})}
                                        disabled={!editable}
                                        fullWidth
                                    />
                                </Property>

                                <Property title={t('Value')} labelAlignment={'top'}>
                                    <TextField
                                        id={`extra-field-${index}-value`}
                                        data-testid={`extra-field-${index}-value`}
                                        size={'small'}
                                        value={extraField.value || ''}
                                        onChange={(e) => updateExtraField(index, {value: e.target.value})}
                                        disabled={!editable}
                                        fullWidth
                                    />
                                </Property>

                                {
                                    editable && (
                                        <Stack direction={'row'} justifyContent={'flex-end'}>
                                            <Button
                                                variant={'text'}
                                                size={'small'}
                                                color={'error'}
                                                onClick={() => {
                                                    const extraFields = [...(profile.extraFields || [])];
                                                    extraFields.splice(index, 1);
                                                    updateField('extraFields', extraFields);
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </Stack>
                        </Property>
                    );
                })
            }

            <Button
                id={'add-extra-field'}
                startIcon={<AddIcon/>}
                onClick={() => {
                    const extraFields = [...(profile.extraFields || [])];
                    extraFields.push({
                        key: '',
                        value: '',
                        source: CRQLAR_EXTRA_FIELD_SOURCE,
                        updatedAt: new Date(),
                    });
                    updateField('extraFields', extraFields);
                }}
                sx={{
                    alignSelf: 'flex-start',
                }}
            >
                {t('Add extra field')}
            </Button>
        </Stack>
    );
};

export default ExtraFields;
